import { useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
	AudioPreview,
	AudioSectionContainer,
	PlayerStatus,
	PlaylistItem,
	Screen,
	getPlaylistId,
} from 'mytours-read-common'
import { loadAudioPreview, setCurrentTime } from 'mytours-read-common/src/audioplayer/actions'

import { createAudioSound } from './audioSound'
import { DEFAULT_SKIP_SECONDS, getAllSounds, getCurrentSound, onBack, onForward } from './soundController'

export interface StopAudioTrack {
	audioPreview?: AudioPreview
	container?: AudioSectionContainer
	collectionId?: number
	screen?: Screen
}

export interface StopAudio {
	// Whether there is anything to play at all. A stop that leads with no audio still
	// renders a row, so every caller has to be able to bail.
	playable: boolean
	isPlaying: boolean
	isLoading: boolean
	currentTime: number
	duration: number
	playPause: () => Promise<void>
	seek: (seconds: number) => void
	skip: (deltaSeconds: number) => void
	// Builds the sound without playing it, so a caller that has a length to draw can ask
	// for one. Idempotent: the sound belongs to `soundController`, so a second call finds
	// the first one's Howl.
	preload: () => Promise<void>
}

/**
 * One stop's audio, as both of its controls see it.
 *
 * The pill over a collapsed stop's thumbnail and the transport inside the expanded
 * panel are two views of one playback, not two players — press play on the pill, expand
 * the row, and the transport has to already be running at the right position. That only
 * holds while both read the same state and go through the same handlers, which is what
 * this is: neither component decides anything about playback itself.
 *
 * The sound belongs to `soundController`, not to whichever component happens to be
 * mounted. That is what lets a stop keep playing after its row is collapsed and the
 * transport unmounted — nothing here stops a sound on the way out.
 */
export function useStopAudio({ audioPreview, container, collectionId, screen }: StopAudioTrack): StopAudio {
	const dispatch = useDispatch()

	const audioId = audioPreview?.id as number
	const fileId = audioPreview?.file?.id as number
	const playable = Boolean(audioPreview?.file && container)

	// This stop's own playlist entry, not the globally-current one. Time and length are
	// properties of a track, and reading them off `currentAudio` would leave an expanded
	// row showing the position of whichever *other* stop happens to be playing.
	const entry = useSelector((state: { audioplayer: { playlist: Map<string, PlaylistItem> } }) =>
		playable
			? state.audioplayer.playlist.get(getPlaylistId(audioId, container!.id, container!.type, fileId))
			: undefined,
	)

	// Whether this stop is the one the app considers current. Only the loading spinner
	// needs it: a track sits in `Loading` from the moment its entry is created, so a
	// preloaded stop that nobody pressed would otherwise show a spinner in its pill.
	const isCurrent = useSelector(
		(state: { audioplayer: { currentPlaylistId: string } }) =>
			playable &&
			state.audioplayer.currentPlaylistId === getPlaylistId(audioId, container!.id, container!.type, fileId),
	)

	const isPlaying = entry?.playerStatus === PlayerStatus.Playing
	const isLoading = isCurrent && entry?.playerStatus === PlayerStatus.Loading
	const currentTime = entry?.currentTime ?? 0
	const duration = entry?.duration ?? 0

	// Guards a double tap while the sound is still being built: `createAudioSound` awaits
	// a HEAD probe for the mp3 rendition, so two presses in quick succession would
	// otherwise make two Howls for one track and only register the first.
	const startingRef = useRef(false)

	// Builds the sound if this stop has none yet, and returns it. Registering it with
	// `soundController` is `createAudioSound`'s job, so a second call finds the first
	// one's Howl rather than making another.
	const ensureSound = useCallback(async () => {
		if (!playable) return undefined

		const existing = getCurrentSound(audioId, container!, fileId)
		if (existing) return existing
		if (startingRef.current) return undefined

		startingRef.current = true
		try {
			// Before the sound, because the reducer only updates playlist entries that
			// already exist — the status dispatches from the Howl's own handlers are
			// no-ops without this.
			dispatch(loadAudioPreview({ container: container!, audio: audioPreview!, data: { collectionId, screen } }))
			return await createAudioSound(audioPreview!, container!, dispatch)
		} finally {
			startingRef.current = false
		}
	}, [playable, audioId, container, fileId, audioPreview, collectionId, screen, dispatch])

	const playPause = useCallback(async () => {
		if (!playable) return

		const existing = getCurrentSound(audioId, container!, fileId)

		// Playing or paused — and nothing else. A track that is merely loaded has to go
		// through the branch below, or pressing play on a stop that was preloaded when its
		// row opened would start it *on top of* whatever is already playing.
		const started = entry?.playerStatus === PlayerStatus.Playing || entry?.playerStatus === PlayerStatus.Pause

		if (existing && started) {
			// Pause and resume are the only place a control acts on a track it has already
			// started, and they are symmetric — the glyph has flipped to a pause mark, so
			// pressing it must do what it says rather than restarting.
			if (isPlaying) existing.pause()
			else existing.play()
			return
		}

		// A different track, or this one after being stopped. Everything else stops rather
		// than pausing: nothing on a collapsed row shows a paused position, so a
		// half-played track left behind would be indistinguishable from an unplayed one
		// and would then resume from the middle.
		for (const sound of getAllSounds()) {
			if (sound !== existing) sound.stop()
		}

		const sound = existing ?? (await ensureSound())
		sound?.play()
	}, [playable, audioId, container, fileId, entry, isPlaying, ensureSound])

	// Seconds, straight onto the Howl. The other two players go through
	// `seekToPercentage` because their seek bar reports a percentage, and this one
	// reports seconds — converting to a percentage and back would only introduce a
	// rounding error, which is not a rounding error anyone can hear but does leave the
	// number in the store disagreeing with the sound's actual position.
	const seek = useCallback(
		(seconds: number) => {
			if (!playable || !Number.isFinite(seconds) || !Number.isFinite(duration) || duration <= 0) return

			const sound = getCurrentSound(audioId, container!, fileId)
			if (!sound) return

			const target = Math.min(duration, Math.max(0, seconds))
			sound.seek(target)
			// The playing position is only pushed into the store on a timer, which does not
			// run while a track is paused — so a seek on a paused track would leave the bar
			// where the finger let go of it and the time label a minute out of date.
			dispatch(setCurrentTime({ audioId, time: target, container: container!, fileId }))
		},
		[playable, audioId, container, fileId, duration, dispatch],
	)

	const skip = useCallback(
		(deltaSeconds: number) => {
			if (!playable) return

			const report = (time: number) => dispatch(setCurrentTime({ audioId, time, container: container!, fileId }))

			if (deltaSeconds < 0) onBack(audioId, container!, fileId, -deltaSeconds, report)
			else onForward(audioId, duration, container!, fileId, deltaSeconds, report)
		},
		[playable, audioId, container, fileId, duration, dispatch],
	)

	const preload = useCallback(async () => {
		await ensureSound()
	}, [ensureSound])

	return { playable, isPlaying, isLoading, currentTime, duration, playPause, seek, skip, preload }
}

export { DEFAULT_SKIP_SECONDS }
